import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";
import {
	defaultKSK1110_R01DetailDto,
	defaultKSK1110_R01HeaderDto,
	KSK1110_R01DetailDto,
	KSK1110_R01HeaderDto,
} from "./Dto_KSK1110_R01";

export interface KSK1110_R01Filter {
	dateRange: DateRange | undefined;
	inoutClass: "" | "110" | "170";
	includeCompleted: boolean;
}

export interface Store_KSK1110_R01 {
	KSK1110_R01Filter: KSK1110_R01Filter;
	KSK1110_R01PastFilter: KSK1110_R01Filter;
	setKSK1110_R01Filter: (filter: Partial<KSK1110_R01Filter>) => void;
	setKSK1110_R01PastFilter: (filter: Partial<KSK1110_R01Filter>) => void;

	KSK1110_R01List: KSK1110_R01HeaderDto[];
	setKSK1110_R01List: (list: KSK1110_R01HeaderDto[]) => void;
	selectedKSK1110_R01: KSK1110_R01HeaderDto | undefined;
	setSelectedKSK1110_R01: (data: KSK1110_R01HeaderDto | undefined) => void;
	initSelectedKSK1110_R01: () => void;

	editKSK1110_R01: KSK1110_R01DetailDto;
	setEditKSK1110_R01: (data: KSK1110_R01DetailDto) => void;

	KSK1110_R01DetailList: KSK1110_R01DetailDto[];
	setKSK1110_R01DetailList: (list: KSK1110_R01DetailDto[]) => void;
	updateKSK1110_R01DetailList: (valueIndex: number, data: KSK1110_R01DetailDto) => void;
	originalKSK1110_R01DetailList: KSK1110_R01DetailDto[];
	setOriginalKSK1110_R01DetailList: (list: KSK1110_R01DetailDto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;
	initData: () => void;
}

const createDefaultKSK1110_R01Filter = (): KSK1110_R01Filter => ({
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	inoutClass: "",
	includeCompleted: true,
});

export const defaultKSK1110_R01Filter: KSK1110_R01Filter = createDefaultKSK1110_R01Filter();

const createIndexedHeaderList = (list: KSK1110_R01HeaderDto[]): KSK1110_R01HeaderDto[] =>
	list.map((item, index) => ({
		...defaultKSK1110_R01HeaderDto,
		...item,
		valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
		orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
		orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
	}));

const createIndexedDetailList = (list: KSK1110_R01DetailDto[]): KSK1110_R01DetailDto[] =>
	list.map((item, index) => ({
		...defaultKSK1110_R01DetailDto,
		...item,
		valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
		orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
		orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
	}));

export const useKSK1110_R01Store = create<Store_KSK1110_R01>((set) => ({
	KSK1110_R01Filter: createDefaultKSK1110_R01Filter(),
	KSK1110_R01PastFilter: createDefaultKSK1110_R01Filter(),
	setKSK1110_R01Filter: (filter) => set((state) => ({
		KSK1110_R01Filter: { ...state.KSK1110_R01Filter, ...filter },
	})),
	setKSK1110_R01PastFilter: (filter) => set((state) => ({
		KSK1110_R01PastFilter: { ...state.KSK1110_R01PastFilter, ...filter },
	})),

	KSK1110_R01List: [],
	setKSK1110_R01List: (list) => set({ KSK1110_R01List: createIndexedHeaderList(list) }),
	selectedKSK1110_R01: undefined,
	setSelectedKSK1110_R01: (data) => set({ selectedKSK1110_R01: data }),
	initSelectedKSK1110_R01: () => set({ selectedKSK1110_R01: undefined }),

	editKSK1110_R01: { ...defaultKSK1110_R01DetailDto },
	setEditKSK1110_R01: (data) => set({ editKSK1110_R01: data }),

	KSK1110_R01DetailList: [],
	setKSK1110_R01DetailList: (list) => set({ KSK1110_R01DetailList: createIndexedDetailList(list) }),
	updateKSK1110_R01DetailList: (valueIndex, data) => set((state) => ({
		KSK1110_R01DetailList: state.KSK1110_R01DetailList.map((item) =>
			item.valueIndex === valueIndex ? data : item),
	})),
	originalKSK1110_R01DetailList: [],
	setOriginalKSK1110_R01DetailList: (list) => set({
		originalKSK1110_R01DetailList: createIndexedDetailList(list).map((item) => ({ ...item })),
	}),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	initData: () => set({
		KSK1110_R01Filter: createDefaultKSK1110_R01Filter(),
		KSK1110_R01PastFilter: createDefaultKSK1110_R01Filter(),
		KSK1110_R01List: [],
		selectedKSK1110_R01: undefined,
		editKSK1110_R01: { ...defaultKSK1110_R01DetailDto },
		KSK1110_R01DetailList: [],
		originalKSK1110_R01DetailList: [],
		columnState: null,
		detailColumnState: null,
		scrollTop: null,
		detailScrollTop: null,
		allCount: 0,
	}),
}));
